import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/api/queryKeys';
import { flattenBlockCatalog } from '../adapters/formsAdapters';
import { formsApi } from '../api/formsApi';
import type { FormConditionWrite, FormLookupSource } from '../api/types';

export function useBlockCatalog() {
  return useQuery({
    queryKey: keys.forms.blockCatalog,
    queryFn: async ({ signal }) => {
      const response = await formsApi.getBlockCatalog({ signal });
      return {
        ...response,
        catalog: flattenBlockCatalog(response),
      };
    },
    staleTime: Infinity,
  });
}

export function useFormLookup(source: FormLookupSource, q: string, enabled = q.length >= 2) {
  return useQuery({
    queryKey: keys.forms.lookup(source, q),
    queryFn: ({ signal }) => formsApi.lookup(source, q, { signal }),
    enabled,
    staleTime: 60_000,
  });
}

export function useFormConditions(formId: string | null | undefined) {
  return useQuery({
    queryKey: keys.forms.conditions(formId ?? ''),
    queryFn: ({ signal }) => formsApi.getConditions(formId!, { signal }),
    enabled: formId != null && formId !== '',
    staleTime: 60_000,
  });
}

export function useCreateFormConditionMutation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormConditionWrite) => formsApi.createCondition(formId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.conditions(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(formId) });
    },
  });
}

export function useUpdateFormConditionMutation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conditionId, data }: { conditionId: string; data: Partial<FormConditionWrite> }) =>
      formsApi.patchCondition(formId, conditionId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.conditions(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(formId) });
    },
  });
}

export function useDeleteFormConditionMutation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conditionId: string) => formsApi.deleteCondition(formId, conditionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.conditions(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(formId) });
    },
  });
}
